import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useAsync } from '../../lib/useAsync';
import { useToast } from '../../lib/toast';
import { MONTH_NAMES, dateTimeLabel } from '../../lib/format';
import type { FinPlLine, FinReport, FinReportDetail } from '../../lib/financeTypes';
import { Banner, EmptyState, Field, LoadingPage, Tag } from '../../components/ui';
import { Segmented, StatusTag, fileSize, usd } from './shared';
import { History as Timeline } from './History';

const periodText = (r: FinReport) =>
  r.periodYear && r.periodMonth ? `${MONTH_NAMES[r.periodMonth - 1]} ${r.periodYear}` : 'Period not detected';

export function Statements() {
  const { notify, fail } = useToast();
  const list = useAsync(async () => (await api.finReports()).reports, []);
  const [openId, setOpenId] = useState<string | null>(null);
  const [view, setView] = useState<'uploads' | 'timeline'>('uploads');
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const reports = list.data ?? [];
  const anyProcessing = reports.some((r) => r.status === 'processing');

  // Poll while something is being read.
  useEffect(() => {
    if (!anyProcessing) return undefined;
    const timer = window.setInterval(() => void list.reload(), 3000);
    return () => window.clearInterval(timer);
  }, [anyProcessing, list]);

  async function upload(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    try {
      const { report } = await api.finUpload(file);
      notify('Statement uploaded. Reading it now.', 'success');
      await list.reload();
      setOpenId(report.id);
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        notify(error.message, 'error');
      } else {
        fail(error, 'That statement could not be uploaded.');
      }
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  if (openId) {
    return <ReportReview id={openId} onBack={() => { setOpenId(null); void list.reload(); }} />;
  }

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Segmented
          label="View"
          value={view}
          onChange={setView}
          options={[
            { value: 'uploads', label: 'Uploads' },
            { value: 'timeline', label: 'Timeline & compare' },
          ]}
        />
      </div>
      {view === 'timeline' && <Timeline />}
      {view === 'uploads' && (
      <>
      <div className="card">
        <div className="card-head"><h3>Upload a monthly statement</h3></div>
        <p className="mut">
          Upload the monthly report from your management company (PDF, .xlsx or CSV). It is stored encrypted and private.
          The reporting period is read from the file, you confirm it, then you review the figures before anything is published.
        </p>
        <input
          ref={fileInput}
          type="file"
          accept=".pdf,.xlsx,.csv,application/pdf,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          disabled={busy}
          onChange={(event) => void upload(event.target.files?.[0])}
          aria-label="Monthly statement file"
        />
        {busy && <p className="mut">Uploading…</p>}
      </div>

      {list.loading && !list.data ? (
        <LoadingPage label="Loading statements…" />
      ) : reports.length === 0 ? (
        <EmptyState title="No statements yet." hint="Upload your first monthly report above." />
      ) : (
        <div className="card tw">
          <h3>Statements</h3>
          <table>
            <thead>
              <tr>
                <th>Period</th>
                <th>Version</th>
                <th>Status</th>
                <th>File</th>
                <th>Uploaded by</th>
                <th>Needs a look</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id}>
                  <td>{periodText(r)}</td>
                  <td>v{r.version}</td>
                  <td><StatusTag status={r.status} />{r.status === 'processing' && <span className="mut"> reading…</span>}</td>
                  <td>
                    <span className="nowrap">{r.filename.length > 36 ? `${r.filename.slice(0, 33)}…` : r.filename}</span>
                    <div className="mut">{fileSize(r.size)}{r.pageCount ? ` · ${r.pageCount} pages` : ''}</div>
                  </td>
                  <td>
                    {r.uploadedByName ?? '—'}
                    <div className="mut">{dateTimeLabel(r.createdAt)}</div>
                  </td>
                  <td>
                    {r.issueCounts.error > 0 && <Tag tone="bad">{r.issueCounts.error} error{r.issueCounts.error === 1 ? '' : 's'}</Tag>}{' '}
                    {r.issueCounts.warning > 0 && <Tag tone="warn">{r.issueCounts.warning} warning{r.issueCounts.warning === 1 ? '' : 's'}</Tag>}
                  </td>
                  <td className="num">
                    <button type="button" className="btn s" onClick={() => setOpenId(r.id)}>
                      {r.status === 'review-required' ? 'Review' : 'Open'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ review */

type PreviewTab = 'issues' | 'pl' | 'balance' | 'bank' | 'ledger' | 'history';

function ReportReview({ id, onBack }: { id: string; onBack: () => void }) {
  const { notify, fail } = useToast();
  const state = useAsync(() => api.finReport(id), [id]);
  const [tab, setTab] = useState<PreviewTab>('issues');
  const [working, setWorking] = useState(false);
  const data = state.data;
  const status = data?.report.status;

  useEffect(() => {
    if (status !== 'processing') return undefined;
    const timer = window.setInterval(() => void state.reload(), 2500);
    return () => window.clearInterval(timer);
  }, [status, state]);

  if (!data) return state.loading ? <LoadingPage label="Loading the statement…" /> : <button type="button" className="btn s" onClick={onBack}>Back</button>;

  const { report } = data;
  const canReview = report.status === 'review-required';

  async function run(action: () => Promise<unknown>, success: string, after?: () => void) {
    setWorking(true);
    try {
      await action();
      notify(success, 'success');
      if (after) after();
      else await state.reload();
    } catch (error) {
      fail(error, 'That did not work.');
    } finally {
      setWorking(false);
    }
  }

  async function download() {
    try {
      const { url } = await api.finDownloadLink(id);
      // The link works for two minutes, for this signed-in user only.
      window.location.assign(url);
    } catch (error) {
      fail(error, 'Could not create a download link.');
    }
  }

  return (
    <div>
      <div className="row between" style={{ marginBottom: 12 }}>
        <button type="button" className="btn s ghost" onClick={onBack}>← All statements</button>
        <div className="btn-row" style={{ margin: 0 }}>
          <button type="button" className="btn s" onClick={() => void download()}>Download original</button>
          <a className="btn s" href={api.finReportExportUrl(id)}>Export figures</a>
          {canReview && (
            <button
              type="button"
              className="btn s danger"
              disabled={working}
              onClick={() => {
                const reason = window.prompt('Why is this statement being rejected?');
                if (reason && reason.trim().length >= 3) void run(() => api.finReject(id, reason.trim()), 'Statement rejected. It will not appear on the dashboards.');
              }}
            >
              Reject
            </button>
          )}
          {(report.status === 'failed' || canReview) && (
            <button type="button" className="btn s" disabled={working} onClick={() => void run(() => api.finReprocess(id), 'Reading the statement again.')}>
              Read again
            </button>
          )}
          {!['approved', 'superseded'].includes(report.status) && (
            <button
              type="button"
              className="btn s danger"
              disabled={working}
              onClick={() => {
                if (window.confirm('Delete this upload and its stored file? This cannot be undone.')) {
                  void run(() => api.finDeleteReport(id), 'Statement deleted.', onBack);
                }
              }}
            >
              Delete
            </button>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>{data.periodLabel} · v{report.version}</h3>
          <StatusTag status={report.status} />
        </div>
        <p className="mut" style={{ margin: 0 }}>
          {report.filename} · {fileSize(report.size)}{report.pageCount ? ` · ${report.pageCount} pages` : ''}
          {report.detectedAssociation ? ` · ${report.detectedAssociation}` : ''}
        </p>
        {report.status === 'processing' && <Banner kind="info">Reading the statement. This usually takes under a minute.</Banner>}
        {report.status === 'failed' && <Banner kind="error">{report.error ?? 'The statement could not be read.'}</Banner>}
        {report.status === 'superseded' && <Banner kind="info">A newer version of this period was approved. This one is kept for the record.</Banner>}
        {report.status === 'rejected' && <Banner kind="error">Rejected. It is kept on the timeline and does not feed any dashboard.</Banner>}
        {report.status === 'approved' && (
          <Banner kind="ok">
            Approved{report.approvedByName ? ` by ${report.approvedByName}` : ''}{report.approvedAt ? ` on ${dateTimeLabel(report.approvedAt)}` : ''}. Dashboards use this statement.
          </Banner>
        )}
      </div>

      {data.summary && (
        <>
          {canReview && <PeriodConfirm data={data} working={working} setWorking={setWorking} onChanged={() => void state.reload()} />}
          <SummaryCards data={data} />
          <div style={{ margin: '12px 0' }}>
            <Segmented
              label="Preview"
              value={tab}
              onChange={setTab}
              options={[
                { value: 'issues', label: `Checks (${data.issues.length})` },
                { value: 'pl', label: 'Income & expenses' },
                { value: 'balance', label: 'Balance sheet' },
                { value: 'bank', label: 'Bank reconciliation' },
                { value: 'ledger', label: 'Ledgers' },
                { value: 'history', label: 'History' },
              ]}
            />
          </div>
          {tab === 'issues' && <Issues data={data} onChanged={() => void state.reload()} />}
          {tab === 'pl' && <PlPreview data={data} editable={canReview} onChanged={() => void state.reload()} />}
          {tab === 'balance' && <BalancePreview data={data} />}
          {tab === 'bank' && <BankPreview data={data} />}
          {tab === 'ledger' && <LedgerPreview id={id} />}
          {tab === 'history' && <History data={data} />}
        </>
      )}

      {canReview && <ApproveBox data={data} working={working} onApprove={(input) => run(() => api.finApprove(id, input), 'Statement approved and published to the dashboards.')} />}
    </div>
  );
}

function PeriodConfirm({
  data,
  working,
  setWorking,
  onChanged,
}: {
  data: FinReportDetail;
  working: boolean;
  setWorking: (v: boolean) => void;
  onChanged: () => void;
}) {
  const { fail, notify } = useToast();
  const r = data.report;
  const [year, setYear] = useState(r.periodYear ?? new Date().getFullYear());
  const [month, setMonth] = useState(r.periodMonth ?? new Date().getMonth() + 1);
  const changed = year !== r.periodYear || month !== r.periodMonth;

  async function save() {
    setWorking(true);
    try {
      await api.finSetPeriod(r.id, { periodYear: year, periodMonth: month, confirm: true });
      notify(`Reporting period set to ${MONTH_NAMES[month - 1]} ${year}.`, 'success');
      onChanged();
    } catch (error) {
      fail(error, 'Could not set the period.');
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className="card">
      <div className="card-head"><h3>Confirm the reporting period</h3></div>
      <p className="mut">
        {r.detectedPeriod ? `The file says: “${r.detectedPeriod}”. ` : 'No period was found in the file. '}
        Confirm the month this statement covers. If one already exists for it, approving will replace it as a new version and keep the old one.
      </p>
      <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Field label="Month">
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {MONTH_NAMES.map((name, index) => <option key={name} value={index + 1}>{name}</option>)}
          </select>
        </Field>
        <Field label="Year">
          <input type="number" min={1990} max={2200} value={year} onChange={(e) => setYear(Number(e.target.value))} />
        </Field>
        <button type="button" className="btn" disabled={working} onClick={() => void save()}>
          {r.periodConfirmed && !changed ? 'Period confirmed ✓' : 'Confirm period'}
        </button>
      </div>
    </div>
  );
}

function SummaryCards({ data }: { data: FinReportDetail }) {
  const s = data.summary;
  if (!s) return null;
  const rows: Array<[string, string]> = [
    ['Operating cash', usd(s.cashOperating)],
    ['Replacement cash', usd(s.cashReplacement)],
    ['Receivables', usd(s.receivables)],
    ['Payables', usd(s.payables)],
    ['Operating net, month', usd(s.operating?.monthNet ?? null)],
    ['Operating net, year to date', usd(s.operating?.ytdNet ?? null)],
    ['Replacement net, year to date', usd(s.replacement?.ytdNet ?? null)],
    ['Owner accounts', String(s.receivablesDetail?.accounts ?? '—')],
  ];
  return (
    <div className="card">
      <div className="card-head">
        <h3>What was read</h3>
        <Tag tone={s.reconciled ? 'ok' : 'warn'}>{s.reconciled ? 'Cash reconciles' : 'Cash not reconciled'}</Tag>
      </div>
      <div className="stat-grid">
        {rows.map(([label, value]) => (
          <div className="stat" key={label}><span className="value">{value}</span><span className="label">{label}</span></div>
        ))}
      </div>
    </div>
  );
}

function Issues({ data, onChanged }: { data: FinReportDetail; onChanged: () => void }) {
  const { fail, notify } = useToast();
  const [code, setCode] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const tone = { error: 'bad', warning: 'warn', info: 'info' } as const;
  if (data.issues.length === 0) return <Banner kind="ok">Every arithmetic check passed. Nothing is flagged.</Banner>;
  return (
    <div className="card tw">
      <p className="mut">A note on a finding never changes the source figure. It sits beside the discrepancy so the next reviewer sees the explanation.</p>
      <table>
        <thead><tr><th>Level</th><th>What was found</th><th>Where</th><th /></tr></thead>
        <tbody>
          {data.issues.map((issue, index) => (
            <tr key={`${issue.code}-${index}`}>
              <td><Tag tone={tone[issue.severity]}>{issue.severity}</Tag></td>
              <td>
                {issue.message}
                {(data.annotations ?? []).filter((n) => n.issueCode === issue.code).map((n) => (
                  <div className="mut" key={n.id}>Note by {n.byName ?? 'a board member'}: {n.note}</div>
                ))}
              </td>
              <td className="mut nowrap">{issue.page ? `page ${issue.page}` : issue.section ?? ''}</td>
              <td className="num"><button type="button" className="btn tiny ghost" onClick={() => { setCode(issue.code); setNote(''); }}>Annotate</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      {code && (
        <div className="row" style={{ gap: 8, marginTop: 10, alignItems: 'flex-end' }}>
          <Field label={`Note on ${code}`}>
            <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} />
          </Field>
          <button
            type="button"
            className="btn s"
            disabled={note.trim().length === 0}
            onClick={async () => {
              try {
                await api.finAnnotate(data.report.id, code, note.trim());
                notify('Note saved. The source value was not changed.', 'success');
                setCode(null);
                onChanged();
              } catch (error) {
                fail(error, 'Could not save the note.');
              }
            }}
          >
            Save note
          </button>
          <button type="button" className="btn s ghost" onClick={() => setCode(null)}>Cancel</button>
        </div>
      )}
    </div>
  );
}

const FIELD_LABELS: Record<string, string> = {
  monthActual: 'Month actual',
  monthBudget: 'Month budget',
  ytdActual: 'YTD actual',
  ytdBudget: 'YTD budget',
  annualBudget: 'Annual budget',
};

function PlPreview({ data, editable, onChanged }: { data: FinReportDetail; editable: boolean; onChanged: () => void }) {
  const { fail, notify } = useToast();
  const [fund, setFund] = useState<'operating' | 'replacement'>('operating');
  const [editing, setEditing] = useState<FinPlLine | null>(null);
  const [field, setField] = useState('monthActual');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const lines = data.lines.filter((l) => l.fund === fund);

  async function save() {
    if (!editing) return;
    setSaving(true);
    try {
      await api.finCorrect(data.report.id, { recordType: 'pl', recordId: editing.id, field, value, reason });
      notify('Correction saved. The checks were run again.', 'success');
      setEditing(null);
      setValue('');
      setReason('');
      onChanged();
    } catch (error) {
      fail(error, 'Could not save the correction.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card tw">
      <div className="row between">
        <Segmented
          label="Fund"
          value={fund}
          onChange={setFund}
          options={[{ value: 'operating', label: 'Operating' }, { value: 'replacement', label: 'Replacement' }]}
        />
        <span className="mut">Source page shown for each line</span>
      </div>
      <table>
        <thead>
          <tr>
            <th>Line</th><th className="num">Month</th><th className="num">Month budget</th>
            <th className="num">YTD</th><th className="num">YTD budget</th><th className="num">Annual</th><th className="num">Page</th>{editable && <th />}
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.id} style={l.kind === 'total' ? { fontWeight: 600 } : undefined}>
              <td>
                {l.label}
                {l.corrected && <Tag tone="info">Corrected</Tag>}
                {l.confidence < 0.8 && <Tag tone="warn">Low confidence</Tag>}
                {l.group && <div className="mut">{l.group}</div>}
              </td>
              <td className="num">{usd(l.monthActual, true)}</td>
              <td className="num">{usd(l.monthBudget, true)}</td>
              <td className="num">{usd(l.ytdActual, true)}</td>
              <td className="num">{usd(l.ytdBudget, true)}</td>
              <td className="num">{usd(l.annualBudget, true)}</td>
              <td className="num mut">{l.page}</td>
              {editable && (
                <td className="num">
                  <button type="button" className="btn tiny ghost" onClick={() => { setEditing(l); setField('monthActual'); setValue(''); setReason(''); }}>Correct</button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {editing && (
        <div className="card flat" style={{ marginTop: 12 }}>
          <h3>Correct “{editing.label}”</h3>
          <p className="mut">The original figure is kept in the history with your reason.</p>
          <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <Field label="Figure">
              <select value={field} onChange={(e) => setField(e.target.value)}>
                {Object.entries(FIELD_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              </select>
            </Field>
            <Field label="New amount" hint="e.g. 1,234.56 or (1,234.56) for a credit">
              <input value={value} onChange={(e) => setValue(e.target.value)} inputMode="decimal" />
            </Field>
            <Field label="Reason">
              <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
            </Field>
            <button type="button" className="btn" disabled={saving || !value || reason.trim().length < 3} onClick={() => void save()}>Save correction</button>
            <button type="button" className="btn ghost" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function BalancePreview({ data }: { data: FinReportDetail }) {
  return (
    <div className="card tw">
      <table>
        <thead><tr><th>Line</th><th className="num">Operating</th><th className="num">Replacement</th><th className="num">All funds</th><th className="num">Page</th></tr></thead>
        <tbody>
          {data.balance.map((b) => (
            <tr key={b.id} style={b.isTotal ? { fontWeight: 600 } : undefined}>
              <td>{b.label}{b.corrected && <Tag tone="info">Corrected</Tag>}<div className="mut">{b.section}</div></td>
              <td className="num">{usd(b.operating, true)}</td>
              <td className="num">{usd(b.replacement, true)}</td>
              <td className="num">{usd(b.allFunds, true)}</td>
              <td className="num mut">{b.page}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BankPreview({ data }: { data: FinReportDetail }) {
  if (data.bankRecs.length === 0) return <EmptyState title="No bank reconciliation was found in this statement." hint="Cash cannot be independently confirmed." />;
  return (
    <>
      {data.bankRecs.map((rec) => {
        const ok = rec.adjustedBankBalance !== null && rec.adjustedBankBalance === rec.adjustedBookBalance;
        return (
          <div className="card" key={rec.id}>
            <div className="card-head"><h3>{rec.accountLabel}</h3><Tag tone={ok ? 'ok' : 'warn'}>{ok ? 'Balances' : 'Does not balance'}</Tag></div>
            <table>
              <tbody>
                {([
                  ['Statement ending balance', rec.statementBalance],
                  ['Deposits in transit', rec.depositsInTransit],
                  ['Outstanding checks and charges', rec.outstandingItems],
                  ['Adjusted bank balance', rec.adjustedBankBalance],
                  ['Book balance', rec.bookBalance],
                  ['Adjustments', rec.adjustments],
                  ['Adjusted book balance', rec.adjustedBookBalance],
                ] as Array<[string, number | null]>).map(([label, v]) => (
                  <tr key={label}><td>{label}</td><td className="num">{usd(v, true)}</td></tr>
                ))}
              </tbody>
            </table>
            {rec.pendingItems.length > 0 && (
              <details style={{ marginTop: 8 }}>
                <summary>{rec.pendingItems.length} outstanding items</summary>
                <table>
                  <tbody>
                    {rec.pendingItems.map((item, index) => (
                      <tr key={index}><td>{item.date ?? ''}</td><td>{item.name}</td><td className="mut">{item.document ?? ''}</td><td className="num">{usd(item.amount, true)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}
          </div>
        );
      })}
    </>
  );
}

function LedgerPreview({ id }: { id: string }) {
  const [kind, setKind] = useState<'gl' | 'ap' | 'ar'>('gl');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const state = useAsync(() => api.finLedger(id, kind, q, offset), [id, kind, q, offset]);
  const rows = state.data?.rows ?? [];
  const total = state.data?.total ?? 0;
  // [key, heading, kind] where kind is 'money', 'plain' (right aligned) or undefined (text).
  const columns: Record<typeof kind, Array<[string, string, ('money' | 'plain')?]>> = {
    gl: [['postedOn', 'Date'], ['vendor', 'Vendor'], ['description', 'Description'], ['account', 'Account'], ['debit', 'Debit', 'money'], ['credit', 'Credit', 'money'], ['page', 'Page', 'plain']],
    ap: [['entryOn', 'Date'], ['vendor', 'Vendor'], ['description', 'Description'], ['charges', 'Charges', 'money'], ['payments', 'Payments', 'money'], ['balance', 'Balance', 'money'], ['page', 'Page', 'plain']],
    ar: [['owner', 'Owner'], ['accountNumber', 'Account'], ['property', 'Property'], ['beginning', 'Opening', 'money'], ['charges', 'Charges', 'money'], ['payments', 'Payments', 'money'], ['ending', 'Balance', 'money'], ['page', 'Page', 'plain']],
  };
  const fmt = (v: unknown, type?: 'money' | 'plain') =>
    v === null || v === undefined ? '' : type === 'money' && typeof v === 'number' ? usd(v, true) : String(v);

  return (
    <div className="card tw">
      <div className="row between" style={{ gap: 8, flexWrap: 'wrap' }}>
        <Segmented
          label="Ledger"
          value={kind}
          onChange={(v) => { setKind(v); setOffset(0); }}
          options={[{ value: 'gl', label: 'General ledger' }, { value: 'ap', label: 'Payables' }, { value: 'ar', label: 'Owner accounts' }]}
        />
        <input placeholder="Search" aria-label="Search the ledger" value={q} onChange={(e) => { setQ(e.target.value); setOffset(0); }} style={{ maxWidth: 220 }} />
      </div>
      {kind === 'ar' && <p className="mut">Owner names are shown to Board Members only and every view is logged.</p>}
      <table>
        <thead><tr>{columns[kind].map(([k, label, type]) => <th key={k} className={type ? 'num' : undefined}>{label}</th>)}</tr></thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={String(row.id ?? index)}>
              {columns[kind].map(([k, , type]) => (
                <td key={k} className={type ? 'num' : undefined}>{fmt(row[k], type)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="row between" style={{ marginTop: 8 }}>
        <span className="mut">{total === 0 ? 'No rows' : `${offset + 1}–${Math.min(offset + 50, total)} of ${total}`}</span>
        <div className="btn-row" style={{ margin: 0 }}>
          <button type="button" className="btn s" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 50))}>Previous</button>
          <button type="button" className="btn s" disabled={offset + 50 >= total} onClick={() => setOffset(offset + 50)}>Next</button>
        </div>
      </div>
    </div>
  );
}

function History({ data }: { data: FinReportDetail }) {
  return (
    <>
      {data.corrections.length > 0 && (
        <div className="card tw">
          <h3>Manual corrections</h3>
          <table>
            <thead><tr><th>Field</th><th className="num">Was</th><th className="num">Now</th><th>Reason</th><th>By</th></tr></thead>
            <tbody>
              {data.corrections.map((c) => (
                <tr key={c.id}>
                  <td>{FIELD_LABELS[c.field] ?? c.field}</td>
                  <td className="num">{c.original ?? '—'}</td>
                  <td className="num">{c.corrected ?? '—'}</td>
                  <td>{c.reason}</td>
                  <td className="mut">{c.byName ?? ''}<div>{dateTimeLabel(c.createdAt)}</div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="card">
        <h3>Activity</h3>
        {data.events.map((e) => (
          <div className="note" key={e.id}>
            <div className="note-head"><span className="who">{e.kind.replace(/-/g, ' ')}</span><span className="mut">{e.actorName ?? 'System'} · {dateTimeLabel(e.createdAt)}</span></div>
          </div>
        ))}
      </div>
    </>
  );
}

function ApproveBox({
  data,
  working,
  onApprove,
}: {
  data: FinReportDetail;
  working: boolean;
  onApprove: (input: { confirmPeriod: true; acknowledgeErrors: boolean; note?: string }) => Promise<void>;
}) {
  const [confirmed, setConfirmed] = useState(false);
  const [ack, setAck] = useState(false);
  const r = data.report;
  const errors = r.issueCounts.error;
  const ready = r.periodConfirmed && confirmed && (errors === 0 || ack);

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="card-head"><h3>Approve and publish</h3></div>
      <p className="mut">
        Approving makes these figures the official numbers behind every dashboard. Nothing is published until you do this.
      </p>
      {!r.periodConfirmed && <Banner kind="warn">Confirm the reporting period above first.</Banner>}
      <label className="row" style={{ gap: 8, alignItems: 'flex-start', margin: '8px 0' }}>
        <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
        <span>I have reviewed the figures and confirm this statement covers <strong>{data.periodLabel}</strong>.</span>
      </label>
      {errors > 0 && (
        <label className="row" style={{ gap: 8, alignItems: 'flex-start', margin: '8px 0' }}>
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
          <span>I understand {errors} check{errors === 1 ? '' : 's'} failed and I am approving anyway.</span>
        </label>
      )}
      <button
        type="button"
        className="btn"
        disabled={!ready || working}
        onClick={() => void onApprove({ confirmPeriod: true, acknowledgeErrors: ack })}
      >
        Approve statement
      </button>
    </div>
  );
}
